import React, { useMemo } from "react";
import { Modal } from "react-bootstrap";
import FullCalendar from "@fullcalendar/react";
import listPlugin from "@fullcalendar/list";
import bootstrap5Plugin from "@fullcalendar/bootstrap5";
import interactionPlugin from "@fullcalendar/interaction";
import allLocales from "@fullcalendar/core/locales-all";
import { EventClickArg, EventContentArg, EventInput } from "@fullcalendar/core";
import { useTranslation } from "react-i18next";
import { CheckCircleFill, CircleFill } from "react-bootstrap-icons";
import dayjs from "../../../../helpers/dayjs";
import { useWMSchedules } from "../../../../api/weekend";
import { CongName, useUserMap } from "../../common";
import { nameOfUser } from "../../../../helpers/user";
import {
  calendarLocale,
  dateToString,
  ISODateFormat,
  ISOMonthFormat,
  localizedMonthYearYYYYMM,
  wmDate,
} from "../../../../helpers/dateHelpers";
import { Congregation, TalkMod, WMSchedule } from "../../../../types/scheduling/weekend";
import { Cong } from "../../../../types/cong";
import { QueryStatus } from "../../../queryStatus";
import { Dayjs } from "dayjs";
import { ISODateString } from "../../../../types/date";
import { noMeetingEvents } from "../wm_common";
import { EventTypes } from "../../../../types/scheduling/events";
import { useCongSettings } from "../../../../api/cong";

interface ScheduleOverviewModalProps {
  startMonth: Date;
  show: boolean;
  onHide: () => void;
  langGroupId: number;
  onNavigateToDate: (date: string) => void;
  congregation: Cong | Congregation | null;
}

interface ScheduleEventProps {
  schedule: WMSchedule;
  actualDate: ISODateString;
}

// Transform WMSchedule data into FullCalendar events
const transformToCalendarEvents = (schedules: WMSchedule[], congregation: Cong | Congregation | null): EventInput[] => {
  if (!congregation) return [];

  return schedules.map((schedule) => {
    // Check if this is a no-meeting event (assembly, convention, memorial)
    const isNoMeetingEvent = !!schedule.event && noMeetingEvents.has(schedule.event.event);

    // Use wmDate to get the proper weekend meeting date based on congregation settings
    const actualDate = wmDate(schedule.date, congregation, schedule.event ? [schedule.event] : undefined);

    return {
      id: `schedule-${schedule.date}`,
      start: actualDate,
      allDay: true,
      classNames: isNoMeetingEvent ? [] : ["wm-overview-event"],
      extendedProps: {
        schedule: schedule,
        actualDate: actualDate,
      } as ScheduleEventProps,
    };
  });
};

export const ScheduleOverviewModal: React.FC<ScheduleOverviewModalProps> = ({
  startMonth,
  show,
  onHide,
  langGroupId,
  onNavigateToDate,
  congregation,
}) => {
  const { t, i18n } = useTranslation();
  const userMap = useUserMap();
  const congSettings = useCongSettings(langGroupId);

  const fromDate = dayjs(startMonth).startOf("month").format(ISODateFormat);
  const toDate = dayjs(fromDate).add(12, "month").endOf("month").format(ISODateFormat);

  const monthsToDisplay = useMemo(() => {
    const start = dayjs(fromDate);
    const months: Dayjs[] = [];

    for (let month = start; months.length < 12; month = month.add(1, "month")) {
      months.push(month);
    }
    return months;
  }, [fromDate]);

  // Fetch all schedules for the 12-month period
  const schedulesQuery = useWMSchedules(fromDate, toDate, langGroupId, { enabled: show });

  // Transform schedules to calendar events
  const allEvents = useMemo(
    () => transformToCalendarEvents(schedulesQuery.data ?? [], congregation),
    [schedulesQuery.data, congregation],
  );

  const queryStatus = QueryStatus(schedulesQuery);
  if (queryStatus !== null) {
    return (
      <Modal show={show} onHide={onHide} size="xl">
        <Modal.Header closeButton>
          <Modal.Title>{t("general.overview")}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{queryStatus}</Modal.Body>
      </Modal>
    );
  }

  if (!schedulesQuery.data) return null;

  // Filter events by month for performance
  const filterEventsForMonth = (events: EventInput[], month: dayjs.Dayjs) => {
    const monthStart = month.startOf("month").format(ISODateFormat);
    const monthEnd = month.endOf("month").format(ISODateFormat);

    return events.filter((e) => {
      const eventStart = typeof e.start === "string" ? e.start : dateToString(e.start as Date, false);
      return eventStart >= monthStart && eventStart <= monthEnd;
    });
  };

  // Custom event rendering to show chairman and speaker status
  const eventContent = (eventInfo: EventContentArg) => {
    const { schedule } = eventInfo.event.extendedProps as ScheduleEventProps;

    // Check if this is a no-meeting event (assembly, convention, memorial)
    const isNoMeetingEvent = !!schedule.event && noMeetingEvents.has(schedule.event.event);

    // For no-meeting events, show event title
    if (isNoMeetingEvent) {
      const eventTitle = schedule.event!.custom_title || t(EventTypes[schedule.event!.event]);
      return (
        <div className="w-100">
          <div className="fc-event-title">{eventTitle}</div>
        </div>
      );
    }

    const chairmanId = schedule.wm_chairman;
    const isChairmanAssigned = chairmanId > 0;
    const chairmanUser = isChairmanAssigned ? userMap.get(chairmanId) : undefined;
    const chairmanName = chairmanUser ? nameOfUser(chairmanUser) : undefined;

    const isSpeakerAssigned = !!schedule.speaker && !!schedule.speaker.id;
    const speakerName = isSpeakerAssigned ? nameOfUser(schedule.speaker) : "";

    const NoSpeakerContent = () => {
      switch (schedule.talk_mod) {
        case TalkMod.TBD:
          return (
            <div>
              <CircleFill size={10} color="gray" className="shrink-0 me-1" />
              {t("schedules.weekend.tbd")}
              {!!schedule.temp_cong && (
                <>
                  <span className="ms-2 me-2">—</span>
                  {CongName(schedule.temp_cong)}
                </>
              )}
            </div>
          );
        case TalkMod.Stream:
          return (
            <>
              <CircleFill size={10} color="green" className="shrink-0 mt-1" />
              {t("schedules.stream")}
            </>
          );
        case TalkMod.CO:
          return (
            <>
              <CircleFill size={10} color="green" className="shrink-0 mt-1" />
              {congSettings.data?.circuit_overseer_name || t("general.circuit-overseer")}
            </>
          );
        default:
          return (
            <>
              <CircleFill size={10} color="red" className="shrink-0 mt-1" />
              <span className="text-muted">{t("schedules.weekend.speaker.0")}</span>
            </>
          );
      }
    };

    const MissingChairmanContent = () => {
      return (
        <>
          <CircleFill size={10} color="red" className="shrink-0 mt-1" />
          <span className="text-muted">{t("schedules.chairman")}</span>
        </>
      );
    };

    return (
      <div className="w-100">
        <div className="d-flex align-items-start gap-1 mb-1">
          {isChairmanAssigned ? (
            <>
              <CircleFill size={10} color="green" className="shrink-0 mt-1" />
              <span className="text-truncate" title={chairmanName}>
                {chairmanName}
              </span>
            </>
          ) : (
            <MissingChairmanContent />
          )}
        </div>
        <div className="d-flex align-items-start gap-1">
          {isSpeakerAssigned ? (
            <>
              {schedule.confirmed ? (
                <CheckCircleFill size={10} color="green" className="shrink-0 mt-1" />
              ) : (
                <CircleFill size={10} color="green" className="shrink-0 mt-1" />
              )}
              <span className="text-truncate">
                {speakerName}
                {!!schedule.speaker?.congregation && (
                  <>
                    <span className="ms-2 me-2">—</span>
                    {CongName(schedule.speaker.congregation)}
                  </>
                )}
              </span>
            </>
          ) : (
            <NoSpeakerContent />
          )}
        </div>
      </div>
    );
  };

  // Handle event clicks
  const handleEventClick = (clickInfo: EventClickArg) => {
    const { schedule } = clickInfo.event.extendedProps as ScheduleEventProps;
    onNavigateToDate(schedule.date);
    onHide();
  };

  return (
    <Modal show={show} onHide={onHide} size="xl">
      <Modal.Header closeButton>
        <Modal.Title>{t("general.overview")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <style>{`
          .wm-overview-event {
            background-color: transparent !important;
            border: none !important;
          }
          .wm-overview-event .fc-event-main {
            color: inherit !important;
            padding: 2px !important;
          }
          /* Hide "all-day" text in list view */
          .fc-list-event-time {
            display: none !important;
          }
          /* Hide the blue dot/marker in list view */
          .fc-list-event-dot {
            display: none !important;
          }
          /* Remove hover highlighting */
          .fc-list-event:hover td {
            background-color: transparent !important;
          }
          /* Remove gray background from day headings */
          .fc-list-day-cushion {
            background-color: transparent !important;
            color: inherit !important;
          }
        `}</style>
        <div
          style={{
            maxHeight: "50vh",
            overflowY: "auto",
            border: "1px solid #dee2e6",
            borderRadius: "0.375rem",
            padding: "1rem",
          }}
        >
          {monthsToDisplay.map((month) => (
            <div key={month.format(ISOMonthFormat)} className="mb-4">
              <h6 className="text-center mb-2">
                {localizedMonthYearYYYYMM(month.year(), month.month() + 1, i18n.language)}
              </h6>
              <FullCalendar
                plugins={[listPlugin, bootstrap5Plugin, interactionPlugin]}
                initialView="listMonth"
                initialDate={month.toDate()}
                height="auto"
                headerToolbar={false}
                events={filterEventsForMonth(allEvents, month)}
                eventContent={eventContent}
                eventClick={handleEventClick}
                locales={allLocales}
                locale={calendarLocale(i18n.language)}
                selectable={false}
              />
            </div>
          ))}
        </div>
      </Modal.Body>
    </Modal>
  );
};
